import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseWithIdList } from '../../../shared/models/list';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
    BaseHttpService, Create, Delete,
    GetById, Patch, Query, Update,
    UriBuilderParams
} from '../base/base-http.service';
import { HttpServiceBundle } from '../base/http-service-bundle.service';
import { DrQueryParams, OpData } from '../base/base-service-with-bundle.service';
import { BackendUriUnits } from '../base/backend-uri-units';
import {
    CaseWorkflowSections,
    CaseWorkflowSettingsTypes,
    Workflow,
    WorkflowTargets
} from '../../../shared/models/workflow';
import * as _ from 'lodash-es';
import { environment } from '../../../../environments/environment';
import { DRResponse } from '../../../shared/models/response';
import { HasId } from '../../../shared/models/base';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';


export class WorkflowUriBuilderParams extends UriBuilderParams<Workflow> {
    private readonly _target: string;

    get target() {
        return this._target;
    }

    constructor(target: WorkflowTargets, workflow?: Workflow | string) {
        super(workflow);
        this._target = target;
    }

    public buildUri(): string {
        let uri = `/${BackendUriUnits.WORKFLOW}/${BackendUriUnits.WORKFLOWS}`;
        uri += this.simpleUriBuild(this.target);
        return uri;
    }
}

@Injectable({
    providedIn: 'root'
})
export class WorkflowService extends BaseHttpService<Workflow, WorkflowUriBuilderParams>
                                 implements Query<Workflow, WorkflowUriBuilderParams>,
                                            GetById<Workflow, WorkflowUriBuilderParams>,
                                            Create<Workflow, WorkflowUriBuilderParams>,
                                            Update<Workflow, WorkflowUriBuilderParams>,
                                            Patch<Workflow, WorkflowUriBuilderParams>,
                                            Delete<Workflow, WorkflowUriBuilderParams> {
    constructor(httpServiceBundle: HttpServiceBundle, protected translateService: TranslateService) {
        super(Workflow.getSubTypeConstructor, httpServiceBundle, true);
    }

    get TABS() {
        return {
            [WorkflowTargets.SMP_CASE]: {
                [CaseWorkflowSections.DEFAULTS]: [{
                    code: CaseWorkflowSettingsTypes.PARTICIPANTS,
                    label: marker('detailed_processing.navigation.participants'),
                    hide: false
                },{
                    code: CaseWorkflowSettingsTypes.DISTRIBUTIONS,
                    label: marker('detailed_processing.navigation.distribution'),
                    hide: false
                }]
            }
        };
    }

    public query(uriParams: WorkflowUriBuilderParams, params?: DrQueryParams): Observable<BaseWithIdList<Workflow>> {
        return this.queryBase(uriParams, params)
                   .pipe(map(result => {
               const list = new BaseWithIdList(Workflow.getSubTypeConstructor({ target: uriParams.target }), result.items);
               list.items = this.sortWorkflows(list.items);
               return list;
           })
        );
    }

    public patchWorkflowTarget(uriParams: WorkflowUriBuilderParams, data: OpData[]): Observable<boolean> {
        const uri = this.constructUri(uriParams);
        return this.http.patch<DRResponse<boolean>>(uri, data)
                        .pipe(map(result => result.data));
    }

    public getById(uriParams: WorkflowUriBuilderParams, params?: DrQueryParams): Observable<Workflow> {
        return this.getBaseById(uriParams, params);
    }

    public create(uriParams: WorkflowUriBuilderParams, workflow: Workflow, params?: DrQueryParams): Observable<Workflow> {
        return this.createBase(uriParams, workflow, params);
    }

    public update(uriParams: WorkflowUriBuilderParams, workflow: Workflow, params?: DrQueryParams): Observable<Workflow> {
        return this.updateBase(uriParams, workflow, params);
    }

    public patch(uriParams: WorkflowUriBuilderParams, data: OpData[], params?: DrQueryParams): Observable<Workflow> {
        return this.patchBase(uriParams, data, params);
    }

    public patchAndUpdate(uriParams: WorkflowUriBuilderParams, workflow: Workflow, data: OpData[], params?: DrQueryParams): Observable<Workflow> {
        return this.patchAndUpdateBase(uriParams, workflow, data, params);
    }

    public delete(uriParams: WorkflowUriBuilderParams): Observable<boolean> {
        return this.deleteBase(uriParams);
    }



    // OTHER METHODS

    public sortWorkflows(workflows: Workflow[]) {
        return _.orderBy(workflows, ['sort_order', 'name'], ['desc', 'asc']);
    }

    public createTargetByWorkflow(target: WorkflowTargets, item: any): Observable<HasId> {
        const uri = `${environment.apiUrl}/${BackendUriUnits.API}/${BackendUriUnits.WORKFLOW}/${target}`;
        return this.http.post<DRResponse<HasId>>(uri, item).pipe(map(result => result.data));
    }

    public loadSection(uriParams: WorkflowUriBuilderParams, workflow: Workflow, section: string): Observable<void> {
        const uri = this.constructUri(uriParams, { urlSuffix: section });
        return this.http.get<DRResponse<any>>(uri)
                        .pipe(map(result => {
            workflow.setSection(section, result.data);
        }));
    }

}
